import ModelOptions from "./ModelOptions";
import { Checkbox } from "../../Components/Form/Checkbox";
import { Dropdown } from "../../Components/Form/Dropdown";
import { InputText } from "../../Components/Form/Input";
import LoaderOverlay from "../../Modules/LoaderOverlay";
import { getComputedDimensionFromCustomProperty } from "../../Utils/CSS";
import { isVisible } from "../../Utils/Elements";
import { destroyPriceRangeFilter, initPriceRangeFilter } from "./PriceRange";

/**
 HACK:
 filtering by key=value1,value2 doesn't work for shopify-cli
 so we have to filter by key=value1 and key=value2
 */
// @ts-ignore
const QUIRK_FILTER_COMMA = !!window.__SHOPIFY_CLI_ENV__;

const SELECTORS = {
    COLLECTION_LISTING: ".collection-listing, .search-results",
    SIDEBAR: "section.product-filters-sidebar",
    HERO: ".shopify-section.collection-hero",
    FORM: "form",
    GRID: ".product-grid",
    FIELDS: "accordion-item:not(.clone) :is(x-dropdown, x-input, x-checkbox)[name]",
    TABS_BAR: ".filter-tabs",
    TABS: ".item a",
    SIDEBAR_OVERLAY: ".product-filters-sidebar-overlay",
    BTN_ELEMENT: "button",
    BTN_OPEN: ".more-filters",
    BTN_CLOSE: ".close",
    ACCORDION_CLONES: "accordion-item.clone [name]",
    SELECT_SORT: ".sorting > x-dropdown",
    PAGE_LINKS: "nav.pagination a"
};

interface IFiltersCacheEntry {
    filters: string;
    summary: string;
    sorting: string;
    tiles: string;
    pagination: string;
    hero: string;
}

interface IFiltersCache {
    [key: string]: IFiltersCacheEntry;
}

class FiltersSidebar {
    private buffer: {
        currentField: InputText | Checkbox | Dropdown;
        cache: IFiltersCache;
    };
    private query: string = Shopify.queryParams.q || "";
    private sorting: string = Shopify.queryParams.sort_by || "";
    private page: string = Shopify.queryParams.page || "1";

    private node: HTMLElement;
    private form: HTMLFormElement;
    private grid: HTMLDivElement;
    private hero: HTMLElement;

    private abortController: AbortController;
    private abortSignal: AbortSignal;
    private domParser: DOMParser;
    private loaderOverlay: LoaderOverlay;
    private modelOptions: ModelOptions;

    private formChangeHandler: () => void;
    private submitHandler: () => void;

    private sectionSuffix = ["-section-grid", "-section-sidebar"];

    private filterOffsetTop: number;

    constructor(node: HTMLElement) {
        this.node = node;
        this.buffer = {
            currentField: null,
            cache: {}
        };
        this.form = this.node.querySelector(SELECTORS.FORM);
        this.grid = this.node.parentElement.querySelector(SELECTORS.GRID);
        this.hero = document.querySelector(SELECTORS.HERO);
        this.domParser = new DOMParser();
        this.loaderOverlay = new LoaderOverlay();
        this.modelOptions = new ModelOptions();

        this.init();
    }

    /**
     * initialize
     * @private
     */
    private init() {
        this.cleanAccordion();
        this.observeBtnOpenIntersection();
        this.initPreselection();
        this.initTabs();
        this.initGrid();
        this.initHandlers().addListeners();
        this.fetchModelOptions().catch(console.error);
    }

    private initPreselection() {
        const locationCategory = location.pathname.split("/").pop();
        if (this.tabsCategories.hasOwnProperty(locationCategory)) return;
        if (!this.selectVendor) return;
        const vendorOptionValues = (this.selectVendor.options as HTMLOptionElement[]).map((option) => option.value);
        const parts = locationCategory.split("-");
        let part: string = "";
        while (parts.length) {
            if (!part) {
                part = parts.shift();
            } else if (!part.includes(" ")) {
                part = `${part} ${parts.shift()}`;
            } else {
                part = part.replace(" ", "-");
            }
            const booleanFilters = this.fields.filter((item) => item.dataset.uriComponent === part) as Checkbox[];
            if (booleanFilters.length) {
                for (const item of booleanFilters) {
                    item.checked = item.value === "1";
                }
                part = "";
                continue;
            }
            const vendorCandidate = part.toUpperCase();
            if (vendorOptionValues.includes(vendorCandidate)) {
                this.selectVendor.disabled = false;
                this.tabs.find((item) => item.dataset.handle === "all").parentElement.classList.add("active");
                if (!this.selectVendor.selectedOptions.some((item) => item.value === vendorCandidate)) {
                    this.selectVendor.selectedOptions = [
                        { label: vendorCandidate, value: vendorCandidate, idx: 0, disabled: false }
                    ];
                }
                part = "";
            }
        }
    }

    /**
     * initialize handlers
     * @private
     */
    private initHandlers() {
        this.formChangeHandler = this.handleFormChange.bind(this);
        this.submitHandler = this.handleSubmit.bind(this);
        return this;
    }

    /**
     * add event listeners
     * @private
     */
    private addListeners() {
        this.node.querySelector(SELECTORS.BTN_CLOSE).addEventListener("click", () => (this.visibility = false));
        this.node.parentElement
            .querySelector(SELECTORS.SIDEBAR_OVERLAY)
            .addEventListener("click", () => (this.visibility = false));

        this.form.addEventListener("formchange", this.formChangeHandler);
        this.form.addEventListener("submit", this.submitHandler);

        return this;
    }

    /**
     * handle form change events
     * @param e
     * @private
     */
    private handleFormChange(e: CustomEvent) {
        this.buffer.currentField = e.detail;
        this.updateHiddenRangeValues();
        // TODO add/remove accordion header tags
        this.page = "1";
        this.update(window.innerWidth >= 1280).catch(console.error);
    }

    /**
     * put range values to hidden fields
     * @private
     */
    private updateHiddenRangeValues() {
        if (
            this.buffer.currentField.name.endsWith("_group_max") ||
            this.buffer.currentField.name.endsWith("_group_min")
        ) {
            const name = this.buffer.currentField.name.slice(0, -4);
            // get the elements
            const hidden = this.form.querySelector<InputText>(`[name="${name}"]`);
            const min = this.form.querySelector<Dropdown>(`[name="${name}_min"]`);
            const max = this.form.querySelector<Dropdown>(`[name="${name}_max"]`);

            // swap if min > max
            if (min.value && max.value && min.selectedIndex > max.selectedIndex) {
                const tmp = max.selectedIndex;
                max.selectedIndex = min.selectedIndex;
                min.selectedIndex = tmp;
            }

            // update the hidden field
            let values = min.dataset.optionsAll
                .split(",")
                .slice(min.value ? min.selectedIndex : 0, max.value ? max.selectedIndex : undefined)
                .join(",");
            if (values === min.dataset.optionsAll) values = "";
            if (values === hidden.value) return;
            hidden.value = values;
        }
        // see PriceRange.ts for price range handling
    }

    /**
     * handle form submit events
     * @param event
     */
    private handleSubmit = (event: SubmitEvent) => {
        event.preventDefault();
        this.update(true)
            .then(() => (this.visibility = false))
            .catch(console.error);
    };

    /**
     * obtain model options by paginating the section api
     * @private
     */
    private async fetchModelOptions() {
        this.form.removeEventListener("formchange", this.formChangeHandler);

        const select = this.selectModel;
        while (select?.options.length) {
            select.removeChild(this.selectModel.querySelector("option"));
        }

        if (this.selectVendor?.selectedOptions.length) {
            this.loaderOverlay.render();
            const { categories, filter_entries } = this.filterValues;

            const path = this.calculatePath(categories, filter_entries);
            const params = new URLSearchParams(filter_entries).toString().split("%2C").join(",");

            const prefix = "product-" + (path.startsWith("/search") ? "search" : "collection");
            try {
                const options = await this.modelOptions.fetch(
                    `${path}?sections=${prefix}-section-model-filter-options&${params}`,
                    `${prefix}-section-model-filter-options`
                );
                this.appendModelOptions(options);
            } catch (error) {
                console.error(error);
            } finally {
                this.handleModelOptionFetchReady();
            }
        } else {
            this.handleModelOptionFetchReady();
        }
    }

    /**
     * append loaded options to model selector
     * @param options
     * @private
     */
    private appendModelOptions(options: HTMLOptionElement[]) {
        if (options.length > 0) {
            options.forEach((o) => this.selectModel.appendChild(o));
            this.selectModel.disabled = false;
            this.selectModelOptionsByURL();
        }
    }

    /**
     * reselect model options after load
     * @private
     */
    private selectModelOptionsByURL() {
        const selectedOptions = location.search
            .split("?")
            .pop()
            .split("&")
            .filter((i) => i.split("=").shift().includes("product.model"));
        let options: { label: string; value: string }[];
        if (QUIRK_FILTER_COMMA) {
            options = selectedOptions.map((i) => {
                const value = decodeURIComponent(i.split("=").pop().replace(/\+/g, " "));
                return { label: value, value: value };
            });
        } else {
            options = selectedOptions[0]
                ?.split("=")
                .pop()
                .split(",")
                .map((i) => {
                    const value = decodeURIComponent(i.replace(/\+/g, " "));
                    return { label: value, value: value };
                });
        }
        if (options?.length) {
            this.selectModel.selectedOptions = options;
        }
    }

    /**
     * handle model options loaded and ready
     * @private
     */
    private handleModelOptionFetchReady() {
        this.form.addEventListener("formchange", this.formChangeHandler);
        this.loaderOverlay.destroy();
        this.disableTabsBySelectedVendor();
    }

    /**
     * workaround to disable tabs if selected vendor does not provide any models in the tabs category
     * @private
     */
    private disableTabsBySelectedVendor() {
        if (this.tabsBar) {
            const vendorMapOption = this.selectModel?.querySelector<HTMLOptionElement>(
                "option[data-category-vendor-map]"
            );
            const categoryVendorMapRaw = vendorMapOption?.dataset.categoryVendorMap;
            const currentVendors = this.selectVendor?.selectedOptions.map((o) => o.value);
            const tabs = this.tabs.filter((t) => t.dataset.filter !== "-");
            tabs.forEach((t) => {
                t.dataset.disabled = t.dataset.productsCount === "0" ? "true" : "false";
            });
            if (categoryVendorMapRaw && currentVendors.length) {
                const category_vendor_map = JSON.parse(categoryVendorMapRaw);
                const map: { [key: string]: string[] } = {};
                category_vendor_map.forEach((cv: string) => {
                    const parts = cv.split(":");
                    const vendor = parts[0];
                    const cat = parts[1];
                    map[vendor] = [...(map[vendor] || []), cat];
                });
                tabs.forEach((t) => {
                    if (t.dataset.disabled === "false") {
                        t.dataset.disabled = "true";
                        currentVendors.forEach((v) => {
                            if (map[v]?.includes(t.dataset.filter)) {
                                t.dataset.disabled = "false";
                            }
                        });
                    }
                });
                delete vendorMapOption.dataset.categoryVendorMap;
            }
        }
    }

    /**
     * remove name attributes from cloned form fields
     * @private
     */
    private cleanAccordion() {
        for (const clone of this.form.querySelectorAll(SELECTORS.ACCORDION_CLONES)) {
            clone.removeAttribute("name");
        }
    }

    /**
     * initialize tabs bar
     * @private
     */
    private initTabs() {
        if (this.tabsBar) {
            this.scrollCategory().then(/**/);
        }
    }

    /**
     * initialize products grid
     * @private
     */
    private initGrid() {
        this.abortController = new AbortController();
        this.abortSignal = this.abortController.signal;

        const sortSelect = this.selectSort;
        sortSelect.addEventListener(
            "change",
            () => {
                this.sorting = sortSelect.value;
                this.page = "1";
                this.update(true).catch(console.error);
            },
            { signal: this.abortSignal }
        );

        const pageLinks = this.grid.querySelectorAll<HTMLAnchorElement>(SELECTORS.PAGE_LINKS);
        for (const p of pageLinks) {
            p.addEventListener(
                "click",
                (e: MouseEvent) => {
                    e.preventDefault();
                    this.page = p.search
                        .split("&")
                        .find((s) => s.includes("page"))
                        .split("=")
                        .pop();
                    this.update(true).catch(console.error);
                },
                { signal: this.abortSignal }
            );
        }

        if (this.tabsBar) {
            for (const t of this.tabs) {
                t.addEventListener(
                    "click",
                    (e: MouseEvent) => {
                        e.preventDefault();
                        for (const f of this.tabs) {
                            f.closest(".item").classList.remove("active");
                        }
                        t.closest(".item").classList.add("active");
                        this.page = "1";
                        this.update(true).catch(console.error);
                    },
                    { signal: this.abortSignal }
                );
            }
        }
    }

    /**
     * observer intersection of mobile form reveal btn
     * @private
     */
    private observeBtnOpenIntersection() {
        const btnOpen = this.grid.querySelector(SELECTORS.BTN_OPEN);
        btnOpen.querySelector(SELECTORS.BTN_ELEMENT).addEventListener("click", () => (this.visibility = true));

        // sticky sidebar open button
        new IntersectionObserver(
            (entries) => {
                btnOpen.classList.toggle(
                    "floating",
                    !entries[0].isIntersecting && entries[0].boundingClientRect.top >= 0
                );
            },
            {
                // TODO: use precise value instead of 100px
                rootMargin: "0px 0px -100px 0px"
            }
        ).observe(btnOpen);
    }

    /**
     * load and show updated filtering results
     * @param {String} path
     * @param {String} params
     * @param {Boolean} tiles whether to update product tiles
     */
    private async fetch(path: string, params: string, tiles: boolean) {
        const section_prefix = "product-" + (path.startsWith("/search") ? "search" : "collection");
        const heroSectionId = this.hero.id.slice(16);
        const sectionParam = `sections=${this.sectionSuffix.map((s) => section_prefix + s).join(",")},${heroSectionId}`;
        const section_url = `${path}?${sectionParam}&${params}`;

        let result = this.buffer.cache[section_url];
        if (!result) {
            const response = await fetch(section_url);
            const json = await response.json();

            const grid = this.domParser.parseFromString(json[`${section_prefix}-section-grid`], "text/html");
            const hero = this.domParser.parseFromString(json[`${heroSectionId}`], "text/html");
            this.buffer.cache[section_url] = result = {
                filters: this.domParser
                    .parseFromString(json[`${section_prefix}-section-sidebar`], "text/html")
                    .querySelector(".product-filters-sidebar form").innerHTML,
                summary: grid.querySelector(".summary").innerHTML,
                sorting: grid.querySelector(".sorting").innerHTML,
                tiles: grid.querySelector(".items").innerHTML,
                hero: hero.querySelector(".collection-hero").innerHTML,
                pagination: grid.querySelector(".pagination")?.outerHTML || ""
            };
        }
        this.render(result, tiles);
        this.buffer.currentField = null;
    }

    /**
     * render results
     * @param {Object} result
     * @param {String} result.filters
     * @param {String} result.summary
     * @param {String} result.sorting
     * @param {String} result.tiles
     * @param {String} result.pagination
     * @param {Boolean} tiles
     */
    private render = (result: IFiltersCacheEntry, tiles: boolean) => {
        if (this.priceRangeFields.length === 2) {
            destroyPriceRangeFilter();
        }
        this.form.innerHTML = this.preserveStates(result.filters);
        if (this.priceRangeFields.length === 2) {
            initPriceRangeFilter(this.priceRangeFields[0] as Dropdown, this.priceRangeFields[1] as Dropdown);
        }
        this.cleanAccordion();
        this.fetchModelOptions().catch(console.error);

        if (tiles) {
            this.abortController.abort();
            this.grid.querySelector(".summary").innerHTML = result.summary;
            this.grid.querySelector(".sorting").innerHTML = result.sorting;
            this.grid.querySelector(".items").innerHTML = result.tiles;
            const current_pagination = this.grid.querySelector(".pagination");
            const new_hero = this.domParser
                .parseFromString(result.hero.trim(), "text/html")
                .querySelector("figure") as HTMLElement;
            if (new_hero && this.hero.querySelector("figure")) {
                this.hero.replaceChild(new_hero, this.hero.querySelector("figure"));
            }
            if (result.pagination) {
                const new_pagination = this.domParser
                    .parseFromString(result.pagination, "text/html")
                    .querySelector("nav");
                if (current_pagination) {
                    this.grid.replaceChild(new_pagination, current_pagination);
                } else {
                    this.grid.appendChild(new_pagination);
                }
            } else if (current_pagination) {
                current_pagination.remove();
            }
            this.initGrid();
        }
    };

    /**
     * scroll active category into view
     * @private
     */
    private async scrollCategory() {
        const tabsBar = this.tabsBar;
        if (tabsBar) {
            const active = this.tabs.find((t) => t.parentElement.classList.contains("active"))?.parentElement;
            if (active) {
                const visible = await isVisible(active);
                if (!visible) {
                    // @ts-ignore ts complains about {behavior: 'instant'} which is indeed a valid option @see: https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollTo
                    tabsBar.querySelector(".scroller").scrollTo({ behavior: "instant", left: active.offsetLeft });
                }
            }
        }
    }

    /**
     * scroll to top of tabs bar
     * @private
     */
    private scrollToSidebarTop() {
        const classList = document.body.classList;
        if (classList.contains("scrolled") && classList.contains("down")) {
            const headerHeight = getComputedDimensionFromCustomProperty(
                document.querySelector(":root"),
                "--header-height",
                true
            ) as number;
            this.filterOffsetTop =
                this.node.closest(SELECTORS.COLLECTION_LISTING).getBoundingClientRect().top +
                window.scrollY -
                headerHeight;
            window.scrollTo({
                behavior: "smooth",
                top: this.filterOffsetTop
            });
        }
    }

    /**
     * refresh product tiles/filters
     * @param {Boolean} tiles whether to refresh product tiles
     */
    private update = async (tiles: boolean) => {
        this.loaderOverlay.render();

        this.scrollCategory().then(/**/);
        this.scrollToSidebarTop();

        const { categories, filter_entries } = this.filterValues;

        const path = this.calculatePath(categories, filter_entries);

        if (this.sorting) {
            filter_entries.push(["sort_by", this.sorting]);
        }

        if (this.page !== "1") {
            filter_entries.push(["page", this.page]);
        }

        const params = new URLSearchParams(filter_entries)
            .toString()
            // HACK: searching by multiple values requires unescaped , as separator
            .split("%2C")
            .join(",");

        history.replaceState(null, null, `${path}${params ? "?" : ""}${params}`);

        try {
            await this.fetch(path, params, tiles);
            // Swym Wishlist integration
            document.dispatchEvent(new CustomEvent("swym:collections-loaded"));
        } catch (error) {
            console.error(error);
            this.loaderOverlay.destroy();
        }
    };

    /**
     * reapply states
     * @param {String} filter
     * @returns {String}
     */
    private preserveStates = (filter: string): string => {
        return this.preserveAccordionState(this.preserveModelDropdownState(filter));
    };

    /**
     * reapply open state to accordion items
     * @param {String} filters
     * @returns {String}
     */
    private preserveAccordionState = (filters: string): string => {
        const doc = this.domParser.parseFromString(filters, "text/html");
        doc.querySelectorAll("accordion-item").forEach((i: HTMLElement) => (i.dataset.open = "false"));
        this.form.querySelectorAll('accordion-item[data-open="true"]').forEach((i: HTMLElement) => {
            const item = doc.querySelector<HTMLElement>(`accordion-item[data-section="${i.dataset.section}"]`);
            item.dataset.open = "true";
        });
        return doc.body.innerHTML;
    };

    /**
     * reapply selected options after reload since we don't know what's been selected in backend
     * @param {String} filters
     * @returns {String}
     */
    private preserveModelDropdownState = (filters: string): string => {
        const doc = this.domParser.parseFromString(filters, "text/html");
        const oldField = this.form.querySelector<Dropdown>('[name="filter.p.m.product.model"]');
        if (oldField) {
            oldField.selectedOptions.forEach((o) => {
                doc
                    .querySelector<HTMLOptionElement>(`[name="filter.p.m.product.model"] option[value="${o.value}"]`)
                    ?.setAttribute("selected", "selected");
            });
        }
        return doc.body.innerHTML;
    };

    private get fields(): (InputText | Checkbox | Dropdown)[] {
        return [...this.form.querySelectorAll<InputText | Checkbox | Dropdown>(SELECTORS.FIELDS)];
    }

    private get selectModel(): Dropdown {
        return this.fields.find((f) => f.name.includes("model")) as Dropdown;
    }

    private get selectVendor(): Dropdown {
        return this.fields.find((f) => f.name.includes("vendor")) as Dropdown;
    }

    private get selectSort(): Dropdown {
        return this.grid.querySelector<Dropdown>(SELECTORS.SELECT_SORT);
    }

    private get tabsBar(): HTMLElement {
        return this.grid.querySelector(SELECTORS.TABS_BAR);
    }

    private get tabsCategories(): { [key: string]: string } {
        return Object.fromEntries(this.tabs.map((item) => [item.dataset.handle, item.pathname]));
    }

    /**
     * obtain tab anchors
     * @private
     */
    private get tabs(): HTMLAnchorElement[] {
        return [...(this.tabsBar?.querySelectorAll<HTMLAnchorElement>(SELECTORS.TABS) || [])];
    }

    /**
     * obtain selected categories
     * @private
     */
    private get categories(): [string, string][] {
        const categories = this.tabs
            ?.filter((i) => i.closest(".item").classList.contains("active"))
            .filter((i) => i.dataset.filter != "-")
            .map((i) => [i.dataset.handle, i.dataset.filter]) as [string, string][];

        if (this.specialCollection.length > 0) {
            categories.push(...this.specialCollection);
        }
        return categories;
    }

    private get specialCollection(): [string, string][] | [] {
        const specialCollection = this.tabsBar?.dataset.specialCollection?.split(":");
        return specialCollection?.length === 2 ? [[specialCollection[0], specialCollection[1]]] : [];
    }

    private get currentCategory(): string {
        const path = location.pathname.split("/");
        return path[path.length - 1];
    }

    private get shop(): string {
        return document.querySelector("html").dataset.shop;
    }

    /**
     * obtain price range fields
     * @private
     */
    private get priceRangeFields() {
        return this.fields.filter((field) => field.name.includes("filter.v.price."));
    }

    private set visibility(v: boolean) {
        document.body.classList.toggle("sidebar-open", v);
        this.node.classList.toggle("open", v);
        this.scrollCategory().then(/**/);
    }

    private get filterValues() {
        const filter_entries: string[][] = [];

        for (const field of this.fields) {
            const key = field.name;

            if (key.endsWith("_min") || key.endsWith("_max")) continue;

            let value = "";
            if (field instanceof Checkbox) {
                if (!field.checked) continue;
                value = field.value;
                if (QUIRK_FILTER_COMMA) {
                    for (const p of value.split(",")) {
                        if (!filter_entries.some((i) => i[0] === key && i[1] === p)) {
                            filter_entries.push([key, p]);
                        }
                    }
                    continue;
                } else {
                    const sibling = filter_entries.find((i) => i[0] === key);
                    if (sibling) {
                        sibling[1] += "," + value;
                        continue;
                    }
                }
            } else if (field instanceof Dropdown) {
                if (key.startsWith("filter.v.price.")) {
                    value = field.value;
                    // ignore if value is min/max
                    if (
                        key.endsWith("gte")
                            ? field.selectedIndex === 0
                            : field.selectedIndex === field.options.length - 1
                    ) {
                        continue;
                    }
                } else if (key.includes("model")) {
                    // ignore models that don't match any selected vendor
                    const selectedVendors = this.selectVendor.selectedOptions.map((o) => o.value);
                    const selectedCategories = this.categories?.map((c) => c[1]);
                    const validValues = field.selectedOptions
                        .map((s) => field.options[s.idx])
                        .filter((o: HTMLOptionElement) => {
                            const selectedByVendor = selectedVendors.includes(o.dataset.vendor);
                            const selectedByCategory =
                                selectedCategories.length == 0 || selectedCategories.includes(o.dataset.category);
                            return selectedByVendor && selectedByCategory;
                        })
                        .map((o) => o.value);

                    if (QUIRK_FILTER_COMMA) {
                        filter_entries.push(...validValues.map((option) => [key, option]));
                    } else {
                        validValues.length > 0 && filter_entries.push([key, validValues.join(",")]);
                    }
                } else if (field.selectedOptions.length || field.value) {
                    // remove duplicate values
                    const options = Array.from(new Set(field.selectedOptions.map((i) => i.value.split(",")).flat()));

                    if (QUIRK_FILTER_COMMA) {
                        filter_entries.push(...options.map((option) => [key, option]));
                    } else {
                        options.length > 0 && filter_entries.push([key, options.join(",")]);
                    }
                }
            } else if (QUIRK_FILTER_COMMA && key.endsWith("_group")) {
                field.value && filter_entries.push(...field.value.split(",").map((value) => [key, value]));
            } else {
                value = field.value;
            }

            if (value !== "") filter_entries.push([key, value]);
        }

        return {
            categories: this.categories,
            filter_entries
        };
    }

    /**
     * obtain path
     * @param categories
     * @param filter_entries
     * @private
     */
    private calculatePath(categories: [string, string][], filter_entries: string[][]): string {
        let path;
        const categoryFilter =
            this.shop === "lim-e" ? "filter.p.m.highlights.vehicle_category" : "filter.p.m.product.category";

        // search
        if (this.query) {
            path = `${Shopify.routes.root}search`;

            filter_entries.unshift(["q", this.query], ["type", "product"]);
        }
        // collection page
        else {
            if (categories.length === 1) {
                path = this.getCollectionUrl(categories[0][0]);
            } else {
                if (this.specialCollection.length > 0) {
                    path = this.getCollectionUrl(this.currentCategory);
                } else {
                    path = this.getCollectionUrl("all");
                }

                categories = categories.filter((item) => item[0] !== this.currentCategory);

                if (QUIRK_FILTER_COMMA) {
                    filter_entries.unshift(...categories.map((item) => [categoryFilter, item[1]]));
                } else {
                    categories.length > 0 &&
                        filter_entries.unshift([categoryFilter, categories.map((item) => item[1]).join(",")]);
                }
            }
        }

        return path;
    }

    protected getCollectionUrl(path: string = null) {
        return `${Shopify.routes.root}collections${path ? "/" + path : ""}`;
    }
}

const filters = document.querySelector<HTMLElement>(SELECTORS.SIDEBAR);
if (filters) {
    new FiltersSidebar(filters);
}
